import React, { useEffect, useState } from 'react';
import Grid from '@mui/material/Grid2';
import { 
  Character,
  CharacterSheetInterface,
  TransformInstance,
} from "@dndModels";
import { SizeType } from "@dndModels/Types";
import { SIZE_OPTIONS } from "@dndModels/Constants";

import CommonUtilities from "@utilities/common_utilities";

import SelectStringBox from "@components/input/selects/SelectStringBox";
import DandDCharacterUtilities from "@dndUtilities/char_utilities";
import CenteredMenu from "@components/input/menus/CenteredMenu";
// import FloatMenu from '../../../../../components/input/menus/FloatMenu';

interface Props {
  character: Character;
  char_interface: CharacterSheetInterface;
  disabled: boolean;
  // onChange: () => void;
}

const common_util = CommonUtilities.getInstance();
const char_util = DandDCharacterUtilities.getInstance();

export default function CharacterSizeButton({ 
  character,
  char_interface,
  disabled,
  // onChange,
}: Props) {
  const [fetched, setFetched] = useState(false);
  const [reloading, setReloading] = useState(false);
  const char_int_type = char_interface instanceof TransformInstance ? "Transform" : "Character";
  const [view, setView] = useState(char_interface.overriding_size === 0 ? "Options" : "Override");
  const [override_size, setOverrideSize] = useState(char_interface.overriding_size === 0 ? "Don't Override" : common_util.size_map[char_interface.overriding_size]);
  const [default_size, setDefaultSize] = useState<string>("Medium");
  const [size_options, setSizeOptions] = useState<string[]>([]);
  const [transform_default_size, setTransformDefaultSize] = useState<string>("Medium");
  const [transform_size_options, setTransformSizeOptions] = useState<string[]>([]);

  useEffect(() => {
    if (!fetched) {
      setFetched(true);
      
      // debugger;
      if (char_interface.size_option_features.length > 0) {
        // Put all the options together.
        let ignore_char_size = true;
        let new_size_options = [...char_interface.size_options];
        let new_default_size: SizeType = 0;
        for (const so of char_interface.size_option_features) {
          if (!so.ignore_char_size) {
            ignore_char_size = false;
          }
          if (so.default_size > 0) {
            new_default_size = so.default_size;
          }
          new_size_options.push(...so.size_options);
        }
        if (!ignore_char_size) {
          new_size_options.push(char_interface.base_size);
        }
        new_size_options = new_size_options.filter(o => o !== 0); // Just to be certain
        new_size_options = common_util.unique_array(new_size_options);
        // if (character.overriding_size > 0) {
        //   if (size_options.includes(character.overriding_size)) {
        //     default_size = character.overriding_size;
        //   }
        // }
        if (new_default_size === 0) {
          if (new_size_options.includes(char_interface.base_size)) {
            new_default_size = char_interface.base_size;
          } else {
            // Get the closest included size to it
            const min = Math.min(...new_size_options);
            const max = Math.max(...new_size_options);
            if (char_interface.base_size < min) {
              new_default_size = min as SizeType;
            } else if (char_interface.base_size > max) {
              new_default_size = max as SizeType;
            } else {
              // It's somewhere in the middle.  It's weird that it's not included as an option.  Probably will never actually come here.  Just make it min.
              new_default_size = min as SizeType;
            }
          }
        }
        setSizeOptions(common_util.get_size_strings(new_size_options.sort()));
        setDefaultSize(common_util.size_map[new_default_size]);
      } else if (char_interface.size_options.length > 0) {
        // Put all the options together.
        let new_size_options = [...char_interface.size_options];
        let new_default_size: SizeType = 0;
        new_size_options = new_size_options.filter(o => o !== 0); // Just to be certain
        new_size_options = common_util.unique_array(new_size_options);
        // if (character.overriding_size > 0) {
        //   if (size_options.includes(character.overriding_size)) {
        //     default_size = character.overriding_size;
        //   }
        // }
        if (new_default_size === 0) {
          if (new_size_options.includes(char_interface.base_size)) {
            new_default_size = char_interface.base_size;
          } else {
            // Get the closest included size to it
            const min = Math.min(...new_size_options);
            const max = Math.max(...new_size_options);
            if (char_interface.base_size < min) {
              new_default_size = min as SizeType;
            } else if (char_interface.base_size > max) {
              new_default_size = max as SizeType;
            } else {
              // It's somewhere in the middle.  It's weird that it's not included as an option.  Probably will never actually come here.  Just make it min.
              new_default_size = min as SizeType;
            }
          }
        }
        setSizeOptions(common_util.get_size_strings(new_size_options.sort()));
        setDefaultSize(common_util.size_map[new_default_size]);
      } else if (character.race.source) {
        if (character.race.source.sizes.length === 1 && character.race.source.sizes[0] === 7) {
          if (character.race.char_subrace && character.race.char_subrace.source) {
            setDefaultSize(common_util.size_map[character.base_size]);
            setSizeOptions(common_util.get_size_strings(character.race.char_subrace.source.sizes.sort()));
          }
        } else {
          setDefaultSize(common_util.size_map[character.base_size]);
          setSizeOptions(common_util.get_size_strings(character.race.source.sizes.sort()));
        }
      }
      if (char_interface instanceof TransformInstance && char_interface.transform) {
        let new_size_options: number[] = [];
        if (char_interface.transform.size.use_options && char_interface.transform.size.options) {
          new_size_options = [...char_interface.transform.size.options];
        } else {
          new_size_options.push(char_interface.base_size);
        }
        setTransformSizeOptions(common_util.get_size_strings(new_size_options.sort()));
        setTransformDefaultSize(common_util.size_map[new_size_options[0]]);
      }
    }
  }, [fetched, char_interface, character]);

  useEffect(() => {
    if (reloading) {
      setReloading(false);
    }
  }, [reloading, setReloading]);

  const renderView = () => {
    if (view === "Override") {
      return (
        <Grid
          size={{
            xs: 12
          }}>
          <SelectStringBox 
            name="Base Size"
            options={SIZE_OPTIONS}
            extra_options={["Don't Override"]}
            value={ override_size } 
            disabled={ disabled }
            onChange={ async (changed: string) => {
              setOverrideSize(changed);
              if (changed === "Don't Override") {
                char_interface.overriding_size = 0;
              } else {
                char_interface.overriding_size = common_util.size_to_num_map[changed];
              }
              character.changed();
              await char_util.repopulateCharFeatureBases(character);
            }}
          />
        </Grid>
      );
    } else {
      switch(char_int_type) {
        case "Character": return renderCharacterSize();
        default: return renderTransformSize();
      }
    }
  }

  const renderCharacterSize = () => {
    if (char_interface.size_option_features.length > 0 || char_interface.size_options.length > 0) {
      return (
        <Grid
          size={{
            xs: 12
          }}>
          <SelectStringBox 
            name="Base Size"
            options={size_options}
            value={default_size} 
            disabled={ disabled }
            onChange={async (changed: string) => {
              setDefaultSize(changed);
              const base_size = common_util.size_to_num_map[changed];
              char_interface.base_size = base_size;
              character.changed();
              await char_util.repopulateCharFeatureBases(character);
            }}
          />
        </Grid>
      );
    } else {
      if (size_options.length === 1) {
        return (
          <Grid
            size={{
              xs: 12
            }}>
            <span style={{ fontWeight: "bold" }}>Base Size: { size_options[0] }</span>
          </Grid>
        );
      } else {
        return (
          <Grid
            size={{
              xs: 12
            }}>
            <SelectStringBox 
              name="Base Size"
              options={size_options}
              value={default_size} 
              disabled={ disabled }
              onChange={async (changed: string) => {
                setDefaultSize(changed);
                const base_size = common_util.size_to_num_map[changed];
                char_interface.base_size = base_size;
                character.changed();
                await char_util.repopulateCharFeatureBases(character);
              }}
            />
          </Grid>
        );
      }
    }
  }

  const renderTransformSize = () => {
    if (char_interface instanceof TransformInstance && char_interface.transform) {
      return (
        <Grid
          size={{
            xs: 12
          }}>
          { transform_size_options.length === 1 ?
            <span style={{ fontWeight: "bold" }}>Base Size: { transform_default_size }</span>
          :
            <SelectStringBox 
              name="Base Size"
              options={transform_size_options}
              value={transform_default_size} 
              disabled={ disabled }
              onChange={async (changed: string) => {
                setTransformDefaultSize(changed);
                const base_size = common_util.size_to_num_map[changed];
                char_interface.base_size = base_size;
                character.changed();
                await char_util.repopulateCharFeatureBases(character);
              }}
            />
          }
        </Grid>
      );
    }
    return null;
  }
  
  const renderModifier = () => {
    // console.log(`Base Size: ${char_interface.base_size}`);
    // console.log(`Mod: ${char_interface.size_modifier}`);
    // console.log(`Use: ${char_interface.use_size}`);
    // console.log(`Override: ${char_interface.overriding_size}`);
    if (char_interface.size_modifier !== 0) {
      const diff = char_interface.size_modifier;
      return (
        <Grid
          size={{
            xs: 12
          }}>Size is { diff > 0 ? `increased` : `decreased` } { diff }size { diff > 1 || diff < -1 ? "categories" : "category" }
        </Grid>
      );
    }
    return null;
  }

  // const options = ["Character","Transform"];
  return (
    <Grid container spacing={0} direction="row">
      <Grid
        className="centered header"
        size={{
          xs: 12
        }}>
        Manage Size
      </Grid>
      <Grid
        size={{
          xs: 12
        }}>
        <CenteredMenu
          options={["Options","Override"]}
          selected={view}
          onSelect={setView}
        />
      </Grid>
      { renderView() }
      { renderModifier() }
    </Grid>
  );
}
